import { useEffect, useRef, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Loader2 } from "lucide-react";
import { ChatMessage } from "@/components/ChatMessage";
import { toast } from "sonner";

function useChatThread(tenantId, onRead) {
  const [data, setData] = useState(null);
  const onReadRef = useRef(onRead);
  useEffect(() => { onReadRef.current = onRead; }, [onRead]);

  const load = useCallback(async (quiet = false) => {
    try {
      const { data: d } = await api.get(`/chat/${tenantId}/messages`);
      setData(d);
      onReadRef.current?.();
    } catch (e) { if (!quiet) toast.error(apiErr(e)); }
  }, [tenantId]);

  useEffect(() => {
    setData(null);
    load();
    const t = setInterval(() => load(true), 8000);
    return () => clearInterval(t);
  }, [load]);

  return { data, load };
}

function ThreadHeader({ other }) {
  return (
    <div className="px-5 py-3.5 border-b border-slate-200 flex items-center gap-3">
      <div className="h-9 w-9 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-bold">{(other?.name || "?").slice(0, 2).toUpperCase()}</div>
      <div className="min-w-0"><div className="text-sm font-semibold text-slate-900 truncate" data-testid="chat-other-name">{other?.name}</div><div className="text-xs text-slate-500 truncate">{other?.email}</div></div>
    </div>
  );
}

function Composer({ tenantId, onSent }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      await api.post(`/chat/${tenantId}/messages`, { body: text });
      setBody("");
      await onSent();
    } catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };
  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };
  return (
    <div className="border-t border-slate-200 p-3 flex gap-2 items-end bg-white">
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={onKey} rows={1} placeholder="Write a message… (Enter to send)"
        className="min-h-[44px] max-h-32 resize-none" data-testid="chat-input" />
      <Button onClick={send} disabled={sending || !body.trim()} className="h-11 gap-2 shrink-0" data-testid="chat-send">
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
      </Button>
    </div>
  );
}

export function ChatThread({ tenantId, myRole, onRead }) {
  const { data, load } = useChatThread(tenantId, onRead);
  const bottomRef = useRef(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ block: "end" }); }, [data?.messages?.length]);

  if (!data) return <div className="flex-1 flex items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const other = myRole === "landlord" ? data.tenant : data.landlord;

  return (
    <div className="flex flex-col h-full" data-testid="chat-thread">
      <ThreadHeader other={other} />
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-slate-50/60" data-testid="chat-messages">
        {data.messages.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-10" data-testid="chat-empty">No messages yet — say hello. Notices you send also appear here.</p>
        )}
        {data.messages.map((m) => <ChatMessage key={m.id} m={m} myRole={myRole} />)}
        <div ref={bottomRef} />
      </div>
      <Composer tenantId={tenantId} onSent={() => load(true)} />
    </div>
  );
}
